<script lang="ts" setup>
import type { TeacherArticle } from '~/models/Content/TeacherArticle'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import useSubscriptionsStore from '~/stores/subscriptions'
import { useAuthStore } from '~/stores/auth'
import { GradeType } from '~/models/GradeType'
import useSubscription from '~/composables/useSubscription'
import useSlugify from '~/composables/useSlugify'
import useSettings from '~/composables/useSettings'
import { useGreetings } from '~/composables/useGreetings'
import Button from '~/components/ui/Button.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'

const { article } = defineProps<{ article?: TeacherArticle }>()

const { t } = useI18n()
const { selectedGradeType, isTeacher, user } = storeToRefs(useAuthStore())
const { relevantSubscription, isLoadingSubscriptions } = storeToRefs(useSubscriptionsStore())
const { toggleSettings, toggleSubscriptions } = useSettings()
const { isSubscriptionPlusOrPremium } = useSubscription()
const { greeting } = useGreetings(GradeType.Grade8To10)
const { slugify } = useSlugify()

const openSubscriptionTab = () => {
  toggleSettings()
  toggleSubscriptions()
}
</script>

<template>
  <div
    v-if="!isLoadingSubscriptions"
    class="relative flex h-full items-center rounded-xl bg-green-50 px-10 py-6 text-base"
  >
    <div
      v-if="relevantSubscription && isSubscriptionPlusOrPremium(relevantSubscription) && isTeacher"
      class="before:border-yellow-40 after:border-yellow-40 absolute -top-2 -left-2 z-10 size-[75px] overflow-hidden before:absolute before:top-0 before:right-0 before:z-0 before:block before:border-4 before:border-t-transparent before:border-l-transparent before:content-[''] after:absolute after:bottom-0 after:left-0 after:z-0 after:block after:border-4 after:border-t-transparent after:border-l-transparent after:content-['']"
    >
      <span class="bg-yellow-pop text-seagreen-50 absolute top-[20px] right-[-69px] z-[1] block w-[225px] -rotate-45 text-center text-sm font-medium uppercase shadow-md">
        {{ t(`subscription.type.${relevantSubscription?.type}`) }}
      </span>
    </div>
    <div class="flex h-full flex-col justify-center  gap-2">
      <p class="mt-auto font-medium md:text-lg">
        {{ greeting.text }}<br>
        <span
          v-if="user"
          class="text-2xl font-semibold md:text-3xl"
          v-text="user.firstName"
        />
      </p>
      <div class="mt-auto flex gap-2">
        <Button
          color="green"
          size="sm"
          @click="toggleSettings()"
        >
          {{ t(`metadata.gradeTypes.short.${selectedGradeType}`) }}
        </Button>
        <Button
          v-if="isTeacher && relevantSubscription?.type"
          color="green"
          size="sm"
          @click="openSubscriptionTab()"
        >
          {{ t(`subscription.type.${relevantSubscription?.type}`).toUpperCase() }}
        </Button>
        <Button
          v-if="isTeacher && article"
          :to="{ name: 'teacher_article', params: { locationId: article.locationId, slug: slugify(article.title) } }"
          class="subscription-button tracking-wide"
          shade="light"
          size="sm"
        >
          Tips & triks
        </Button>
      </div>
    </div>
  </div>
  <AuSkeleton
    v-else-if="isLoadingSubscriptions"
    background="var(--color-green-50)"
    class="flex self-start"
    height="138px"
    width="200px"
  />
</template>
